import { Inject, Injectable } from '@angular/core';
import { StateService } from '@rci/state-service';
import {
  AnalyticsError,
  AnalyticsService,
  ICompleteUniversalDataLayer,
  InteractionEvent,
  Mapping,
} from '@rogers/analytics';
import { IUniversalDataLayer, Notification } from '@rogers/analytics/lib/analytics.model';
import { SelfServeAnalyticsService, TrackErrorData, TrackEventData } from '@rogers/self-serve-analytics';
import { cloneDeep, get, has, merge } from 'lodash';
import { AnalyticsConfiguration } from './analytics-config/analytics-configuration';
import { ANALYTICS_CONFIGURATION } from './analytics-config/analytics-configuration.service';
import { modalPopup } from './analytics-config/global-analytics-configuration';

// eslint-disable-next-line
export function provideSelfServeAnalyticsService() {
  return [
    AnalyticsServiceProxy,
    {
      provide: SelfServeAnalyticsService,
      useClass: AnalyticsServiceProxy,
    },
  ];
}

@Injectable()
export class AnalyticsServiceProxy implements SelfServeAnalyticsService {
  private readonly mappings: Mapping;
  private readonly callBackFunctions: {
    [name: string]: Function;
  };

  constructor(
    @Inject(ANALYTICS_CONFIGURATION) private readonly analyticsConfiguration: AnalyticsConfiguration,
    private readonly analyticsService: AnalyticsService,
    private readonly stateService: StateService,
  ) {
    this.mappings = this.analyticsConfiguration.mappings;
    this.callBackFunctions = this.analyticsConfiguration.callBackFunctions;
  }

  track(_payload: ICompleteUniversalDataLayer, isModalPopup: boolean, _selectedSections, mapping) {
    try {
      const payload = cloneDeep(_payload);
      if (payload && payload.page && isModalPopup) {
        payload.page.url = modalPopup;
      }
      const callbackName = get(mapping, 'callbackName');
      if (!!callbackName && has(this.callBackFunctions, callbackName)) {
        this.callBackFunctions[callbackName](payload);
      }

      if (!!get(payload, 'errors')) {
        this.analyticsService.trackErrors([] as AnalyticsError[], payload);
      } else {
        const isPageView = get(payload, 'events.pageView');
        if (isPageView) {
          this.stateService.setUrl(payload);
        } else {
          this.analyticsService.trackClick({} as InteractionEvent, payload);
        }
      }
    } catch (e) {
      // eslint-disable-next-line no-console
      console.warn('Exception in track', e);
    }
  }

  trackEvent(event: TrackEventData, errors?: AnalyticsError[]) {
    if (!event) {
      return;
    }
    try {
      let payload: ICompleteUniversalDataLayer = cloneDeep(this.mappings[event.mappingKey]) || {};
      if (event.additionalPayload) {
        payload = merge(payload, event.additionalPayload);
      }
      if (event.callBackParam && has(this.callBackFunctions, event.callBackParam)) {
        this.callBackFunctions[event.callBackParam](payload);
      }
      if ((event.pageView || errors) && !payload.events) {
        payload.events = {};
      }
      if (event.pageView) {
        payload.events.pageView = true;
      }
      if (payload.page && event.isModalPopup) {
        payload.page.url = modalPopup;
      }
      if (errors) {
        payload.errors = errors;
        payload.events.error = true;
      }
      if (payload.events?.pageView) {
        this.stateService.setUrl(payload);
      } else {
        this.analyticsService.trackClick({} as InteractionEvent, payload);
      }
    } catch (err) {
      // eslint-disable-next-line no-console
      console.warn('Exception in dataLayer track event directive', err);
    }
  }

  trackError(errorObject: TrackErrorData, errorMessage: string) {
    const { code, api, API, flowType, field, mappingKey, category, callBackParam, type } = errorObject;
    if (
      api === undefined &&
      API === undefined &&
      code === undefined &&
      field === undefined &&
      category === undefined &&
      type === undefined &&
      flowType === undefined
    ) {
      return;
    }
    try {
      const payload: AnalyticsError[] = [
        {
          API: !!api ? api : API,
          code,
          field,
          type: this.getPayloadType(code, type),
          category,
          message: errorMessage,
          flowType,
        },
      ];
      const globalVars = mappingKey ? cloneDeep(this.mappings[mappingKey]) : {};
      if (callBackParam) {
        this.callBackFunctions[callBackParam](globalVars);
      }
      this.analyticsService.trackErrors(payload, globalVars);
    } catch (err) {
      // eslint-disable-next-line no-console
      console.warn('Exception in dataLayer track error directive', err);
    }
  }

  trackNotifications(notifications: Notification[], payload?: IUniversalDataLayer) {
    this.analyticsService.trackNotifications(notifications, payload);
  }

  private getPayloadType(errorCode: string, errorType: string) {
    if (errorType === 'http' && (parseInt(errorCode, 10) === 0 || errorCode === undefined)) {
      /**
       * errorCode === undefined is for form submission where most often
       * errorCode is undefined
       */
      return 'user';
    }

    return errorType;
  }
}
